import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase } from "@/lib/hooks/api/utils";
import { enrichTimeEntryApprovalRow } from "./time-entries-formatters";

export function useApproveTimeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const supabase = getSupabase();

      const { data: existingEntry, error: fetchError } = await supabase
        .from("time_entries")
        .select(
          `
                    id,
                    is_approved,
                    ticket:tickets!inner(id, contact_id)
                `
        )
        .eq("id", id)
        .single();

      if (fetchError || !existingEntry) {
        throw new Error("Time entry not found");
      }

      if (existingEntry.is_approved) {
        throw new Error("Time entry is already approved");
      }

      const user = (await supabase.auth.getUser()).data.user;

      const { data: updatedEntry, error: updateError } = await supabase
        .from("time_entries")
        .update({
          is_approved: true,
          approved_by: user?.id || null,
          approved_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select(
          `
                    *,
                    ticket:tickets!inner(
                        id,
                        ticket_number,
                        title,
                        priority,
                        status
                    ),
                    user:app_users!time_entries_user_id_fkey(
                        id,
                        email,
                        first_name,
                        last_name
                    )
                `
        )
        .single();

      if (updateError) {
        console.error("Error approving time entry:", updateError);
        throw new Error("Failed to approve time entry");
      }

      return enrichTimeEntryApprovalRow(updatedEntry);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["time-entries"] });
      toast.success("Time entry approved successfully");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to approve time entry");
      console.error("Error approving time entry:", error);
    },
  });
}

export function useDisapproveTimeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const supabase = getSupabase();

      const { data: existingEntry, error: fetchError } = await supabase
        .from("time_entries")
        .select(
          `
                    id,
                    is_approved,
                    ticket:tickets!inner(id, contact_id)
                `
        )
        .eq("id", id)
        .single();

      if (fetchError || !existingEntry) {
        throw new Error("Time entry not found");
      }

      if (!existingEntry.is_approved) {
        throw new Error("Time entry is already not approved");
      }

      const { data: updatedEntry, error: updateError } = await supabase
        .from("time_entries")
        .update({
          is_approved: false,
          approved_by: null,
          approved_at: null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select(
          `
                    *,
                    ticket:tickets!inner(
                        id,
                        ticket_number,
                        title,
                        priority,
                        status
                    ),
                    user:app_users!time_entries_user_id_fkey(
                        id,
                        email,
                        first_name,
                        last_name
                    )
                `
        )
        .single();

      if (updateError) {
        console.error("Error disapproving time entry:", updateError);
        throw new Error("Failed to disapprove time entry");
      }

      return enrichTimeEntryApprovalRow(updatedEntry);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["time-entries"] });
      toast.success("Time entry disapproved successfully");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to disapprove time entry");
      console.error("Error disapproving time entry:", error);
    },
  });
}
